"use client";

import { SORT_BY_MENU } from "@/constants/menu";
import {
  DEFAULT_QUERY_SETTING,
  PAGINATION_LIMIT_16,
} from "@/constants/variables";
import useTags from "@/hooks/useTags";
import { IAgentData, IAgentFilters } from "@/interfaces/IAgentData";
import { IInfo } from "@/interfaces/IHelper";
import { getAgents } from "@/services/agent.service";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

const useAgent = (props?: Pick<IAgentFilters, "sort_option">) => {
  // State --------------------------------------------------
  const [limit, setLimit] = useState<number>(PAGINATION_LIMIT_16);
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<number>(-1);
  const [sortOption, setSortOption] = useState<string>(
    props?.sort_option || SORT_BY_MENU[0].id,
  );
  const [pageInfo, setPageInfo] = useState<IInfo>();
  const [search, setSearch] = useState<string>("");
  const [tags, setTags] = useState<string[] | undefined>(undefined);
  // This value is used to trigger refetch when filterAgent changes
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [agentsFiltered, setAgentsFiltered] = useState<IAgentData[]>([]);
  // --------------------------------------------------------

  const { allTags } = useTags();

  // Filter agent
  const filterAgent = useMemo(() => {
    return {
      limit,
      page,
      sort,
      sort_option: SORT_BY_MENU.find((e) => e.id === sortOption)?.value,
      search,
      search_option: "name",
      tags,
      is_active: true,
    };
  }, [limit, page, sort, search, tags, sortOption]);

  // Query --------------------------------------------------
  const {
    data: dataAgents,
    isLoading: isDataAgentsLoading,
    // error: dataAgentsError,
    // refetch,
  } = useQuery({
    queryFn: () => getAgents(filterAgent),
    queryKey: ["dataAgents", filterAgent],
    ...DEFAULT_QUERY_SETTING,
    // Enabled this to refetch data when filterAgent changes
    enabled: isFiltering,
  });
  // --------------------------------------------------------

  // USE EFFECT --------------------------------------------------
  useEffect(() => {
    // Do this when filterAgent changes
    if (dataAgents) {
      setAgentsFiltered(dataAgents.data);
      setPageInfo(dataAgents.info);
    }
  }, [dataAgents]);
  // --------------------------------------------------------

  return {
    isDataAgentsLoading,
    agentsFiltered,
    isFiltering,
    search,
    setSearch,
    setIsFiltering,
    setLimit,
    setPage,
    setSort,
    setSortOption,
    setTags,
    allTags,
    pageInfo,
  };
};

export default useAgent;
